import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animate } from 'animejs';

@Component({
  selector: 'fz-search',
  standalone: true,
  imports: [MatIcon, MatButton, FormsModule, ReactiveFormsModule],
  templateUrl: './search.html',
  styleUrls: ['./search.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'fz-search',
    '[class.fz-search--appearance-icon]': 'appearance() === "icon"',
    '[class.fz-search--full-width]': 'isSearchExpanded()',
  },
})
export class Search implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #fb = inject(FormBuilder);

  placeholder = input<string>('Search...');
  searchTerm = input<string | undefined>('');
  opened = input<boolean>(false);
  appearance = input<'icon' | 'default'>('default');
  searchChange = output<string>();
  expandChange = output<boolean>();

  isSearchExpanded = signal(false);

  searchForm = this.#fb.group({
    search: [''],
  });

  /**
   * Reference to the search input element.
   */
  @ViewChild('searchInput') searchInputElementRef: ElementRef;

  constructor() {
    effect(() => {
      const searchTerm = this.searchTerm();

      this.searchForm.setValue({
        search: searchTerm || '',
      });
    });
  }

  ngOnInit() {
    this.searchForm.controls.search.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => {
        this.searchChange.emit(value as string);
      });
  }

  /**
   * Toggles the search expanded state.
   *
   * @param event - The event to prevent propagation and default behavior
   */
  toggleExpanded(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    this.isSearchExpanded.update((isSearchExpanded) => !isSearchExpanded);
    this.expandChange.emit(this.isSearchExpanded());

    if (this.isSearchExpanded()) {
      setTimeout(() => {
        this.searchInputElementRef.nativeElement.focus();
      }, 100);
    }
  }

  formSubmit(event: SubmitEvent) {
    event.stopPropagation();
    event.preventDefault();

    const formData = new FormData(event.target as HTMLFormElement);
    const search = formData.get('search');

    if (search) {
      this.searchChange.emit(search as string);
    }
  }
}
